import tw from 'twin.macro';
import React, { TextareaHTMLAttributes } from 'react';
import { css } from '@emotion/react';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  className?: string;
  autoGrow?: boolean;
  enterBreakLine?: boolean;
  ref?: any;
  onRowsChange?: (rows: number) => void;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      value,
      className,
      children,
      autoGrow,
      enterBreakLine = true,
      onKeyDown,
      onRowsChange,
      ...props
    },
    ref
  ) => {
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const lastHeightRef = React.useRef<number>(0);
    const timeoutRef = React.useRef<NodeJS.Timeout>();

    const handleAutoGrow = (element: HTMLTextAreaElement): void => {
      if (!autoGrow) {
        return;
      }

      // Clear any pending timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set a timeout to debounce the height calculation
      timeoutRef.current = setTimeout(() => {
        const style = window.getComputedStyle(element);
        const lineHeight = parseInt(style.lineHeight) || parseInt(style.fontSize) * 1.2;
        const paddingTop = parseInt(style.paddingTop);
        const paddingBottom = parseInt(style.paddingBottom);

        // Create a clone to calculate scrollHeight without affecting the original
        const clone = element.cloneNode(true) as HTMLTextAreaElement;
        clone.style.position = 'absolute';
        clone.style.visibility = 'hidden';
        clone.style.height = 'auto';
        document.body.appendChild(clone);

        const scrollHeight = clone.scrollHeight;
        document.body.removeChild(clone);

        // Only update if height actually changed
        if (scrollHeight !== lastHeightRef.current) {
          lastHeightRef.current = scrollHeight;
          element.style.height = `${scrollHeight}px`;

          // Calculate rows based on content height
          const contentHeight = scrollHeight - paddingTop - paddingBottom;
          const rows = Math.max(1, Math.round(contentHeight / lineHeight));
          onRowsChange?.(rows);
        }
      }, 50); // 50ms debounce
    };

    React.useEffect(() => {
      textareaRef.current && handleAutoGrow(textareaRef.current);
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
      <textarea
        ref={ref ? ref : textareaRef}
        css={[
          tw`
            border-color-input-box 
            rounded-input          
            appearance-none
            block
            border
            w-full
            p-3
            text-gray-0
            leading-tight
            focus:outline-none`,
          css`
            &::placeholder {
              opacity: 1;
              ${tw`text-color-input-placeholder`}
            }
            &:disabled {
              opacity: 0.45;
            }
          `,
        ]}
        className={className}
        value={value || ''}
        onKeyDown={(e): void => {
          !enterBreakLine && e.keyCode === 13 && e.preventDefault();
          onKeyDown && onKeyDown(e);
        }}
        {...props}
      >
        {children}
      </textarea>
    );
  }
);

export default Textarea;
